<template>
  <div>
    <div>
      <router-view name="notifications" />
    </div>
    <router-view />
    <div class="tw-pointer-events-none tw-fixed tw-top-5 tw-z-[1000] tw-flex tw-w-full tw-flex-col tw-items-center">
      <div class="tw-max-w-[70vw]">
        <div v-for="toast in toasts" :key="toast.id" class="tw-mb-2 tw-flex">
          <StudioToast
            v-if="toast"
            :mode="toast.mode"
            :message="toast.msg"
            :delay="toast.duration"
            :action="toast.action"
            @close="removeToast(toast.id)"
          />
        </div>
      </div>
    </div>
    <ContentLabToast />
  </div>
</template>

<script>
import logger from '@goldcast/logger';
import { Hub } from 'aws-amplify';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';

import store from './store';

import { trackMouseClick } from '@/analytics';
import ContentLabToast from '@/studio/contentLab/ContentLabToast.vue';
import StudioToast from '@/studioComponents/StudioToast.vue';
import { getEnv } from '@/utils/env';

import('@/../tailwind/tailwind-output.css');

export default {
  name: 'App',
  components: { StudioToast, ContentLabToast },
  data() {
    return {
      is_zendesk_enabled: getEnv('VUE_APP_ZENDESK_KEY') !== 'false'
    };
  },
  created() {
    this.watchFutureAuthStateChangeOfUser();
    this.addZendeskScript();
    window.addEventListener('click', trackMouseClick);
  },
  beforeUnmount() {
    window.removeEventListener('click', trackMouseClick);
  },
  computed: {
    ...mapGetters('org', {
      org: 'getCurrentOrg'
    }),
    ...mapGetters('user', {
      currentUser: 'getCurrentUser'
    }),
    ...mapGetters('zendeskWidget', ['isThereNoLiveEventsInOrg']),
    ...mapState('studioToast', { toasts: 'toasts' })
  },
  watch: {
    currentUser(value) {
      if (value && this.is_zendesk_enabled) {
        const zenDeskScript = document.getElementById('ze-snippet');
        if (!zenDeskScript) {
          this.addZendeskScript();
        } else {
          this.showZendeskWidget();
        }
      } else {
        if (window.zE) {
          window.zE('webWidget', 'hide');
        }

        const LOGIN_ROUTES = ['Login', 'Oktalogin', 'Ssologin'];
        if (!value && !LOGIN_ROUTES.includes(this.$route.name)) {
          this.$router.push({ name: 'Login' }).catch(e => {
            logger.error('Error in redirecting to login page: ', e);
          });
        }
      }
    },
    org() {
      this.setZendeskSettings();
      this.showZendeskWidget();
    }
  },
  methods: {
    ...mapActions('user', ['resetUser']),
    ...mapMutations('studioToast', ['removeToast']),
    watchFutureAuthStateChangeOfUser() {
      Hub.listen('auth', capsule => {
        const event = capsule.payload.event;
        if (event === 'signOut') {
          this.resetUser();
        }
      });
    },
    async watchZendeskWidgetState() {
      if (window.zE) {
        const self = this;
        window.zE('webWidget:on', 'open', function () {
          store.commit('zendeskWidget/setZendeskChatState', true);
        });
        window.zE('webWidget:on', 'userEvent', function (event) {
          if (event.action === 'Chat Shown') {
            if (!self.isThereNoLiveEventsInOrg) {
              window.zE('webWidget', 'chat:addTags', ['live_event']);
            } else {
              window.zE('webWidget', 'chat:removeTags', ['live_event']);
            }
          } else if (event.action === 'Web Widget Minimised') {
            store.commit('zendeskWidget/setZendeskChatState', false);
          }
        });
      }
    },
    addZendeskScript() {
      if (this.is_zendesk_enabled && this.currentUser) {
        const externalScript = document.createElement('script');
        externalScript.onload = () => {
          this.watchZendeskWidgetState();
          this.setZendeskSettings();
          this.identifyZendeskUser();
          this.showZendeskWidget();
        };
        externalScript.setAttribute(
          'src',
          `https://static.zdassets.com/ekr/snippet.js?key=${getEnv('VUE_APP_ZENDESK_KEY')}`
        );
        externalScript.setAttribute('id', 'ze-snippet');
        document.head.appendChild(externalScript);
      }
    },
    showZendeskWidget() {
      if (window.zE) {
        window.zE('webWidget:on', 'close', function () {
          window.zE('webWidget', 'hide');
        });
        window.zE('webWidget', 'hide');
      }
    },
    setZendeskSettings() {
      if (this.org && window.zE) {
        const isContentLabStandalone = this.org.is_content_lab_standalone;
        window.zE('webWidget', 'updateSettings', {
          webWidget: {
            chat: {
              suppress: isContentLabStandalone
            },
            contactForm: {
              suppress: !isContentLabStandalone
            }
          }
        });
        const org = this.org;
        window.zE(function () {
          window.zE('webWidget', 'chat:addTags', [org.name]);
        });
      }
    },
    identifyZendeskUser() {
      const self = this;
      if (window.zE) {
        window.zE(function () {
          window.zE.identify({
            name: self.currentUser.first_name.concat(' ', self.currentUser.last_name),
            email: self.currentUser.email
          });
        });
      }
    }
  }
};
</script>

<style>
body {
  --background: 255 255 255;
  --accent: 241 245 249;
  --muted: 241 245 249;
  --muted-foreground: 71 85 105;
  --foreground: 2 6 23;
  --success: 22 163 74;
  --danger: 220 38 38;
}
</style>
